import { useEffect } from "react";

/**
 * Lightweight in-browser <head> updater for the public marketing site.
 *
 * Why roll our own vs. react-helmet-async:
 *   * The public site is a small, always-remounted subtree. We don't need
 *     helmet's nested-priority resolution — the deepest <Seo/> always wins.
 *   * Avoids adding a dependency for the sake of 3 tags.
 *   * Covers the cases that JS-capable consumers (browsers, in-app share
 *     sheets, modern Slack/WhatsApp bots that run JS) actually need.
 *
 * Social crawlers that DO NOT run JS (Facebook, old Twitter, Discord
 * embedder) rely on the meta tags baked into index.html. For those we
 * provide a generic HoneyAcorn set there and override at runtime for
 * anyone who executes JS. A proper multi-tenant prerender story would
 * need either an edge prerender or a per-request SSR layer — out of
 * scope for this iteration.
 *
 * All props are optional; missing ones fall back to sensible defaults.
 */
export default function Seo({
  title,
  description,
  canonical,
  ogImage,
  ogType = "website",
  themeColor,
  siteName,
  jsonLd,
}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const apply = (sel, attr, value) => {
      if (value == null) return () => {};
      let el = document.head.querySelector(sel);
      const created = !el;
      if (created) {
        el = document.createElement(sel.startsWith("meta")
          ? "meta" : sel.startsWith("link") ? "link" : "meta");
        // rebuild each key=val pair from the selector (brittle for arbitrary
        // selectors, fine for ours which follow `meta[property="foo"]` etc.)
        const m = sel.match(/^(meta|link)\[([^=]+)="([^"]+)"\]$/);
        if (m) el.setAttribute(m[2], m[3]);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute(attr);
      el.setAttribute(attr, value);
      return () => {
        if (created) {
          el.remove();
        } else if (prev != null) {
          el.setAttribute(attr, prev);
        }
      };
    };

    const cleanups = [];
    const push = (fn) => fn && cleanups.push(fn);

    push(apply('meta[name="description"]',                'content', description));
    push(apply('meta[property="og:title"]',               'content', title));
    push(apply('meta[property="og:description"]',         'content', description));
    push(apply('meta[property="og:type"]',                'content', ogType));
    push(apply('meta[property="og:site_name"]',           'content', siteName));
    push(apply('meta[property="og:url"]',                 'content', canonical));
    push(apply('meta[property="og:image"]',               'content', ogImage));
    push(apply('meta[name="twitter:card"]',               'content', ogImage ? "summary_large_image" : "summary"));
    push(apply('meta[name="twitter:title"]',              'content', title));
    push(apply('meta[name="twitter:description"]',        'content', description));
    push(apply('meta[name="twitter:image"]',              'content', ogImage));
    push(apply('meta[name="theme-color"]',                'content', themeColor));
    push(apply('link[rel="canonical"]',                   'href',    canonical));

    // JSON-LD structured data (optional) — only set when provided. We
    // carry our own <script> rather than piggybacking on meta so the
    // baked-in HoneyAcorn script in index.html isn't mutated.
    let jsonLdEl = null;
    if (jsonLd) {
      jsonLdEl = document.createElement("script");
      jsonLdEl.type = "application/ld+json";
      jsonLdEl.setAttribute("data-seo", "public-site");
      jsonLdEl.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(jsonLdEl);
    }

    return () => {
      document.title = prevTitle;
      cleanups.forEach((fn) => fn());
      if (jsonLdEl) jsonLdEl.remove();
    };
  }, [title, description, canonical, ogImage, ogType, themeColor, siteName, jsonLd]);

  return null;
}
